import { useQuery } from "@tanstack/react-query";
import { bonusQuestionsRepository } from "@/config/dataSource";
import { BONUS_QUESTION_ORDER, hasAnyActiveQuestion, isDeadlinePassed } from "@/modules/leagues/types/bonusQuestions";
import type { LeagueSummary } from "@/modules/leagues/types/league";

export interface PendingBonusQuestion {
  leagueId: string;
  leagueName: string;
  countMissing: number;
}

/**
 * Aviso do dashboard (pedido do usuário): "quando as perguntas forem
 * ativadas todos os usuários receberão em seu dashboard o aviso que ele
 * tem que responder". Verifica, por liga, se há perguntas ativas, ainda
 * dentro do prazo, e com pelo menos uma resposta faltando.
 * Promise.allSettled — uma liga com erro não derruba o aviso das outras
 * (mesmo princípio já aplicado em useUpcomingMatchesAcrossLeagues).
 */
export function usePendingBonusQuestionsAcrossLeagues(leagues: LeagueSummary[] | undefined) {
  return useQuery({
    queryKey: ["pending-bonus-questions", (leagues ?? []).map((l) => l.id).sort().join(",")],
    queryFn: async (): Promise<PendingBonusQuestion[]> => {
      const results = await Promise.allSettled(
        (leagues ?? []).map(async (league) => {
          const [config, myAnswer] = await Promise.all([
            bonusQuestionsRepository.getConfig(league.id),
            bonusQuestionsRepository.getMyAnswer(league.id),
          ]);
          if (!hasAnyActiveQuestion(config) || isDeadlinePassed(config) || config?.scored) return null;
          const activeTypes = BONUS_QUESTION_ORDER.filter((t) => config?.questions[t]?.active);
          const countMissing = activeTypes.filter((t) => !myAnswer?.answers[t]).length;
          if (countMissing === 0) return null;
          return { leagueId: league.id, leagueName: league.nome, countMissing };
        })
      );
      return results
        .filter((r): r is PromiseFulfilledResult<PendingBonusQuestion | null> => r.status === "fulfilled")
        .map((r) => r.value)
        .filter((v): v is PendingBonusQuestion => v !== null);
    },
    enabled: Boolean(leagues) && leagues!.length > 0,
  });
}
